import { ArrowLeft, Shield, Eye, Database, Download, Trash2, MapPin, Activity, Mail, Bell } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { BottomNav } from "./bottom-nav";
import { SettingsSection } from "./settings";
import * as authClient from "@/app/api/auth-client";
import { ACCOUNT_DELETION_CONFIRMATION_PHRASE } from "@/app/copy/account-deletion-public";
import { clearSessionAppRoute } from "@/app/utils/session-app-route";

interface PrivacyPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

/** Toggle rows are not wired to APIs yet — keep disabled until implemented. */
const PRIVACY_CONTROLS_DISABLED = true;

export function PrivacyPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: PrivacyPageProps) {
  // Privacy toggles state
  const [profileVisibility, setProfileVisibility] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [locationTracking, setLocationTracking] = useState(true);
  const [activityTracking, setActivityTracking] = useState(true);
  const [searchHistory, setSearchHistory] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(true);
  const [thirdPartySharing, setThirdPartySharing] = useState(false);

  const handleDownloadData = () => {
    alert(
      "Solicitação de Download de Dados\n\n" +
      "Seus dados serão preparados e enviados para seu e-mail em até 48 horas, conforme a LGPD.\n\n" +
      "Você receberá um arquivo ZIP contendo:\n" +
      "• Dados pessoais\n" +
      "• Histórico de compras\n" +
      "• Anúncios publicados\n" +
      "• Conversas e mensagens"
    );
  };

  const handleDeleteAccount = async () => {
    if (
      !confirm(
        "ATENÇÃO: exclusão de conta e dados\n\n" +
          "Encerramos a sessão, removemos carrinho, favoritos, notificações e anúncios quando possível. " +
          "Pedidos antigos podem ser anonimizados (sem o seu nome) para obrigações fiscais. " +
          "Dados financeiros podem permanecer na Stripe conforme a política dela.\n\n" +
          "Deseja continuar?"
      )
    ) {
      return;
    }
    if (!authClient.getAccessToken()) {
      toast.error("Faça login para solicitar a exclusão.");
      return;
    }
    let hasPassword = true;
    try {
      const me = await authClient.me();
      hasPassword = me.has_password !== false;
    } catch {
      toast.error("Não foi possível validar a sessão. Faça login novamente.");
      return;
    }
    let payload: { password?: string; confirmation?: string };
    if (hasPassword) {
      const pw = window.prompt("Confirme a sua senha:");
      if (pw == null || pw.trim() === "") return;
      payload = { password: pw };
    } else {
      const phrase = window.prompt(
        `Conta sem senha (Google/Apple). Digite exatamente: ${ACCOUNT_DELETION_CONFIRMATION_PHRASE}`
      );
      if (phrase !== ACCOUNT_DELETION_CONFIRMATION_PHRASE) {
        toast.error("Frase de confirmação incorreta.");
        return;
      }
      payload = { confirmation: phrase };
    }
    try {
      await authClient.requestAccountDeletion(payload);
      toast.success("Exclusão processada. Sessão encerrada; consulte a página pública para detalhes.");
      try {
        await authClient.logout();
      } catch {
        authClient.clearTokens();
      }
      clearSessionAppRoute();
      window.location.assign("/");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Não foi possível concluir o pedido.";
      toast.error(msg);
    }
  };

  const handleClearSearchHistory = () => {
    if (confirm("Deseja limpar todo o histórico de buscas?")) {
      alert("Histórico de buscas limpo com sucesso!");
    }
  };

  const privacyControlSection = {
    title: "Controle de Privacidade",
    items: [
      {
        icon: Eye,
        label: "Perfil Público",
        subtitle: profileVisibility ? "Visível para outros usuários" : "Apenas você pode ver",
        action: () => setProfileVisibility((prev) => !prev),
        toggle: true,
        value: profileVisibility,
        disabled: PRIVACY_CONTROLS_DISABLED,
      },
      {
        icon: Mail,
        label: "Mostrar E-mail no Perfil",
        subtitle: showEmail ? "E-mail visível" : "E-mail oculto",
        action: () => setShowEmail((prev) => !prev),
        toggle: true,
        value: showEmail,
        disabled: PRIVACY_CONTROLS_DISABLED,
      },
      {
        icon: Bell,
        label: "Mostrar Telefone no Perfil",
        subtitle: showPhone ? "Telefone visível" : "Telefone oculto",
        action: () => setShowPhone((prev) => !prev),
        toggle: true,
        value: showPhone,
        disabled: PRIVACY_CONTROLS_DISABLED,
      },
    ],
  };

  const dataCollectionSection = {
    title: "Coleta de Dados",
    items: [
      {
        icon: MapPin,
        label: "Rastreamento de Localização",
        subtitle: locationTracking ? "Ativo - melhor experiência" : "Desativado",
        action: () => setLocationTracking((prev) => !prev),
        toggle: true,
        value: locationTracking,
        disabled: PRIVACY_CONTROLS_DISABLED,
      },
      {
        icon: Activity,
        label: "Rastreamento de Atividade",
        subtitle: activityTracking ? "Ativo - recomendações personalizadas" : "Desativado",
        action: () => setActivityTracking((prev) => !prev),
        toggle: true,
        value: activityTracking,
        disabled: PRIVACY_CONTROLS_DISABLED,
      },
      {
        icon: Database,
        label: "Histórico de Buscas",
        subtitle: searchHistory ? "Salvando histórico" : "Não salvar",
        action: () => setSearchHistory((prev) => !prev),
        toggle: true,
        value: searchHistory,
        disabled: PRIVACY_CONTROLS_DISABLED,
      },
    ],
  };

  const communicationSection = {
    title: "Comunicações",
    items: [
      {
        icon: Mail,
        label: "E-mails de Marketing",
        subtitle: marketingEmails ? "Receber promoções" : "Não receber",
        action: () => setMarketingEmails((prev) => !prev),
        toggle: true,
        value: marketingEmails,
        disabled: PRIVACY_CONTROLS_DISABLED,
      },
      {
        icon: Shield,
        label: "Compartilhamento com Terceiros",
        subtitle: thirdPartySharing ? "Permitido" : "Bloqueado",
        action: () => setThirdPartySharing((prev) => !prev),
        toggle: true,
        value: thirdPartySharing,
        disabled: PRIVACY_CONTROLS_DISABLED,
      },
    ],
  };

  const dataManagementSection = {
    title: "Gerenciamento de Dados",
    items: [
      {
        icon: Database,
        label: "Limpar Histórico de Buscas",
        subtitle: "Remove todas as buscas salvas",
        action: handleClearSearchHistory,
      },
      {
        icon: Download,
        label: "Baixar Meus Dados",
        subtitle: "Exportar dados conforme LGPD",
        action: handleDownloadData,
      },
      {
        icon: Trash2,
        label: "Excluir Conta",
        subtitle: "Pedido de exclusão com confirmação de identidade",
        action: () => {
          void handleDeleteAccount();
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Privacidade</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {/* Information Banner */}
        <div className="mx-4 mt-4 bg-secondary/30 border border-primary/20 rounded-xl p-4">
          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-foreground mb-1">
                Seus dados estão protegidos
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Respeitamos sua privacidade conforme a LGPD (Lei Geral de Proteção de Dados).
                Você tem controle total sobre seus dados pessoais.
              </p>
            </div>
          </div>
        </div>

        {/* Settings Sections */}
        <div className="px-4 space-y-6 mt-6">
          <SettingsSection
            title={privacyControlSection.title}
            items={privacyControlSection.items}
          />
          <SettingsSection
            title={dataCollectionSection.title}
            items={dataCollectionSection.items}
          />
          <SettingsSection
            title={communicationSection.title}
            items={communicationSection.items}
          />
          <SettingsSection
            title={dataManagementSection.title}
            items={dataManagementSection.items}
          />
        </div>

        {/* LGPD Information */}
        <div className="px-4 mt-6 mb-8">
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-medium text-foreground mb-2">
              Seus Direitos pela LGPD
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Confirmação da existência de tratamento de dados</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Acesso aos seus dados pessoais</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Correção de dados incompletos ou desatualizados</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Anonimização, bloqueio ou eliminação de dados</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Portabilidade dos dados a outro fornecedor</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Eliminação dos dados tratados com seu consentimento</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">•</span>
                <span>Revogação do consentimento a qualquer momento</span>
              </li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              Para exercer seus direitos, entre em contato através do{" "}
              <span className="text-primary font-medium">suporte@akihub.com.br</span>
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}